'use client';

import { BreakpointsOptions } from '@mui/material';
import { default as MuiZIndex } from '@mui/material/styles/zIndex';

import { alpha } from '../../../utils';
import { BasePalette, BaseTheme, Elevation, Typography } from '../../types';
import { components } from '../components';

export const breakpoints: BreakpointsOptions = {
  values: {
    xs: 360,
    sm: 768,
    md: 1200,
    lg: 1400,
    xl: 1920,
  },
};

export const elevation: Elevation = {
  100: '0px 2px 4px -1px rgba(0, 0, 0, 0.15), 0px 4px 5px rgba(0, 0, 0, 0.08), 0px 1px 10px rgba(0, 0, 0, 0.06);',
  200: '0px 5px 5px -3px rgba(0, 0, 0, 0.15), 0px 8px 10px 1px rgba(0, 0, 0, 0.08), 0px 3px 14px 2px rgba(0, 0, 0, 0.06);',
  300: '0px 8px 10px -5px rgba(0, 0, 0, 0.15), 0px 16px 24px 2px rgba(0, 0, 0, 0.08), 0px 6px 30px 5px rgba(0, 0, 0, 0.06);',
};

/* 
mobile stepper: 1000
fab: 1050
speed dial: 1050
app bar: 1100
drawer: 1200
modal: 1300
snackbar: 1400
tooltip: 1500
*/

export const zIndex = {
  ...MuiZIndex,
  onboardingMenu: 2200,
  onboardingTour: 1600,
};

const typography: Typography = {
  fontFamily: 'inherit',

  display1: { fontSize: 57, lineHeight: '64px', fontWeight: 300 },
  display2: { fontSize: 45, lineHeight: '52px', fontWeight: 300 },
  display3: { fontSize: 36, lineHeight: '44px', fontWeight: 300 },

  headline1: { fontSize: 32, lineHeight: '40px', fontWeight: 350 },
  headline2: { fontSize: 28, lineHeight: '36x', fontWeight: 350 },
  headline3: { fontSize: 24, lineHeight: '32px', fontWeight: 450 },

  title1: { fontSize: 21, lineHeight: '28px', fontWeight: 400 },
  title2: { fontSize: 15, lineHeight: '20px', fontWeight: 600 },
  title3: { fontSize: 14, lineHeight: '24px', fontWeight: 700 },

  label1: { fontSize: 14, lineHeight: '20px', fontWeight: 400 },
  label2: { fontSize: 12, lineHeight: '16px', fontWeight: 600 },
  label3: { fontSize: 11, lineHeight: '16px', fontWeight: 400 },

  body1: { fontSize: 20, lineHeight: '28px', fontWeight: 400 },
  body2: { fontSize: 16, lineHeight: '24px', fontWeight: 400 },
  body3: { fontSize: 14, lineHeight: '20px', fontWeight: 400 },
  body4: { fontSize: 13, lineHeight: '20px', fontWeight: 400 },
  body5: { fontSize: 12, lineHeight: '18px', fontWeight: 400 },
  body6: { fontSize: 14, lineHeight: '20px', fontWeight: 600 },
  body7: { fontSize: 12, lineHeight: '18px', fontWeight: 600 },
  body8: { fontSize: 11, lineHeight: '18px', fontWeight: 600 },
  body9: { fontSize: 13, lineHeight: '20px', fontWeight: 600 },

  button1: {
    fontSize: 15,
    lineHeight: '24px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  button2: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  button3: { fontSize: 16, lineHeight: '24px', fontWeight: 600 },
  button4: { fontSize: 14, lineHeight: '24px', fontWeight: 600 },
  button5: { fontSize: 12, lineHeight: '18px', fontWeight: 600 },
  button6: { fontSize: 16, lineHeight: '24px', fontWeight: 400 },
  button7: { fontSize: 16, lineHeight: '24px', fontWeight: 400 },
};

const palette: BasePalette = {
  blue: {
    50: '#E4F3FF',
    100: '#BDE1FF',
    200: '#91CFFF',
    300: '#60BCFF',
    400: '#3AACFF',
    500: '#1C9CFF',
    600: '#2B8DFF',
    700: '#3179FF',
    800: '#3466EC',
    900: '#1450C8',
  },
  red: {
    50: '#FBEAE9',
    100: '#FFCDC0',
    200: '#FFAD98',
    300: '#FF8D6F',
    400: '#FF7350',
    500: '#FF5B34',
    600: '#F95430',
    700: '#EA4E2B',
    800: '#DC4727',
    900: '#C23B1F',
  },
  yellow: {
    50: '#FFFDE6',
    100: '#FFF8C2',
    200: '#FFF399',
    300: '#FFEF6F',
    400: '#FEEA4D',
    500: '#FCE427',
    600: '#FED527',
    700: '#FCBD20',
    800: '#FBA418',
    900: '#F77A09',
  },
  green: {
    50: '#E5F5E7',
    100: '#C2E5C3',
    200: '#9AD59D',
    300: '#6FC575',
    400: '#4CB957',
    500: '#22AC38',
    600: '#169D2F',
    700: '#008B24',
    800: '#007A18',
    900: '#005C00',
  },
  black: {
    0: '#FEFEFE',
    50: '#EFF1F3',
    '50_a': '#F6F8FA',
    100: '#EAECEE',
    200: '#DFE1E8',
    300: '#CAD0E1',
    400: '#A8B0C4',
    '400_a': '#BDC5D9',
    500: '#7A849D',
    600: '#5B657E',
    '600_a': '#667089',
    '600_b': '#737D96',
    700: '#434A60',
    '700_a': '#4D546A',
    '700_b': '#5A6177',
    800: '#2E3549',
    '800_a': '#353C51',
    900: '#131622',
    '900_a': '#1E2333',
    '900_b': '#252C40',
  },

  get primary() {
    return {
      main: this.blue[700],
      dark: this.blue[800],
      light: this.blue[600],
      contrastText: '#FFF',
    };
  },

  get success() {
    return {
      main: this.green[500],
      dark: this.green[800],
      light: this.green[400],
      contrastText: '#FFF',
    };
  },

  get warning() {
    return {
      main: this.yellow[700],
      dark: this.yellow[800],
      light: this.yellow[600],
      contrastText: '#FFF',
    };
  },

  get info() {
    return {
      main: this.blue[700],
      dark: this.blue[800],
      light: this.blue[600],
      contrastText: '#FFF',
    };
  },

  get error() {
    return {
      main: this.red[800],
      dark: this.red[900],
      light: this.red[700],
      contrastText: '#FFF',
    };
  },

  get stateNormal() {
    return this.primary.main;
  },

  get stateHover() {
    return this.primary.light;
  },

  get stateActive() {
    return this.primary.light;
  },

  get stateDisabled() {
    return alpha(this.black['700_b'] as string, 0.5);
  },

  get shadowTwo() {
    return '0px 5px 5px -3px rgba(0, 0, 0, 0.15), 0px 8px 10px 1px rgba(0, 0, 0, 0.08), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)';
  },

  get shadow() {
    return '0px 5px 5px -3px rgba(0, 0, 0, 0.15), 0px 8px 10px 1px rgba(0, 0, 0, 0.08), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)';
  },
};

export const baseTheme: BaseTheme = {
  palette,
  breakpoints,
  components,
  typography,
  spacing: 4,
  elevation,
  zIndex,
  shape: {
    borderRadius: 2,
  },
};
